import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import theme from '../../theme';
import { fetchRemoteTransactions } from '../transactions/transactionsRepository';
import { useI18n } from '../../i18n/useI18n';
import AppButton from '../../components/AppButton';

function monthKey(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthLabel(date: Date, locale?: string) {
  return date.toLocaleString(locale || undefined, { month: 'short' });
}

type MonthPoint = {
  key: string;
  label: string;
  income: number;
  expense: number;
};

export default function QuarterDashboard({ userId, lang }: { userId?: string; lang?: string }) {
  const { width } = useWindowDimensions();
  const stackSummaryCards = width < 380;
  const [monthly, setMonthly] = useState<MonthPoint[]>([]);
  const [mode, setMode] = useState<'3m' | 'current'>('3m');
  const { t, locale } = useI18n(lang);

  useEffect(() => {
    async function load() {
      try {
        const txns = await fetchRemoteTransactions(userId);
        const now = new Date();
        const months = [
          new Date(now.getFullYear(), now.getMonth() - 2, 1),
          new Date(now.getFullYear(), now.getMonth() - 1, 1),
          new Date(now.getFullYear(), now.getMonth(), 1),
        ];

        const map = new Map<string, { income: number; expense: number; label: string }>();
        for (const m of months) {
          map.set(monthKey(m), { income: 0, expense: 0, label: monthLabel(m, locale) });
        }

        const start = months[0];
        for (const tx of txns || []) {
          const d = new Date(tx.date);
          if (Number.isNaN(d.getTime())) continue;
          if (d < start) continue;
          const key = monthKey(d);
          const entry = map.get(key);
          if (!entry) continue;
          const amt = Number(tx.amount || 0);
          if (tx.type === 'income') entry.income += amt;
          else entry.expense += amt;
        }

        setMonthly(months.map(m => {
          const k = monthKey(m);
          const v = map.get(k)!;
          return { key: k, label: v.label, income: v.income, expense: v.expense };
        }));
      } catch (e) {
        // ignore
      }
    }
    load();
  }, [userId, locale]);

  const visible = mode === 'current' ? monthly.slice(-1) : monthly;
  const summary = visible.reduce(
    (acc, m) => ({ income: acc.income + m.income, expense: acc.expense + m.expense }),
    { income: 0, expense: 0 }
  );

  const maxAbsNet = Math.max(
    1,
    ...visible.map(m => Math.abs(m.income - m.expense))
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}> 
      <Text style={[styles.title, { color: theme.colors.textPrimary }]}>{t('quarterSummary')}</Text>
      <View style={[styles.row, stackSummaryCards ? styles.rowStacked : null]}>
        <View style={[styles.card, stackSummaryCards ? styles.cardStacked : null, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.cardLabel, { color: theme.colors.textSecondary }]}>{t('income')}</Text>
          <Text style={[styles.cardValue, { color: theme.colors.textPrimary }]}>${summary.income.toFixed(2)}</Text>
        </View>
        <View style={[styles.card, stackSummaryCards ? styles.cardStacked : null, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.cardLabel, { color: theme.colors.textSecondary }]}>{t('expense')}</Text>
          <Text style={[styles.cardValue, { color: theme.colors.textPrimary }]}>${summary.expense.toFixed(2)}</Text>
        </View>
        <View style={[styles.card, stackSummaryCards ? styles.cardStacked : null, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.cardLabel, { color: theme.colors.textSecondary }]}>{t('total')}</Text>
          <Text style={[styles.cardValue, { color: theme.colors.textPrimary }]}>${(summary.income - summary.expense).toFixed(2)}</Text>
        </View>
      </View>

      <View style={{ height: 12 }} />
      <View style={[styles.modeRow, stackSummaryCards ? styles.modeRowStacked : null]}>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>{t('last3Months')}</Text>
        <AppButton
          title={mode === 'current' ? t('showLast3Months') : t('showCurrentMonth')}
          onPress={() => setMode(m => (m === 'current' ? '3m' : 'current'))}
          tone="secondary"
          variant="outline"
          style={stackSummaryCards ? { width: '100%' } : undefined}
        />
      </View>

      <View style={[styles.chart, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}>
        {visible.map((m) => {
          const net = m.income - m.expense;
          const h = Math.max(6, Math.round((Math.abs(net) / maxAbsNet) * 72));
          const barColor = net >= 0 ? theme.colors.success : theme.colors.error;
          return (
            <View key={m.key} style={styles.chartCol}>
              <View style={[styles.bar, { height: h, backgroundColor: barColor }]} />
              <Text style={[styles.chartLabel, { color: theme.colors.textSecondary }]}>{m.label}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { padding: 12 },
  title: { fontSize: 16, fontWeight: '700', marginBottom: 8 },
  subtitle: { fontSize: 12, fontWeight: '600' },
  row: { flexDirection: 'row', justifyContent: 'space-between' },
  rowStacked: { flexDirection: 'column', justifyContent: 'flex-start' },
  card: { flex: 1, padding: 10, margin: 4, borderRadius: theme.radii.sm, borderWidth: 1 },
  cardStacked: { flex: undefined, width: '100%' },
  cardLabel: { fontSize: 12 },
  cardValue: { fontSize: 18, fontWeight: '700', marginTop: 6 },
  modeRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  modeRowStacked: { flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' },
  chart: {
    marginTop: 8,
    borderWidth: 1,
    borderRadius: theme.radii.sm,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
  },
  chartCol: { alignItems: 'center', justifyContent: 'flex-end', flex: 1 },
  bar: { width: 22, borderRadius: 6 },
  chartLabel: { marginTop: 6, fontSize: 11 },
});
